// Google Analytics utility functions
import * as Sentry from "@sentry/react";

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
  
  // Node.js process global (may not exist in browser)
  var process: {
    env: {
      NODE_ENV?: string;
      [key: string]: string | undefined;
    };
  } | undefined;
}

export const GA_TRACKING_ID = 'G-J04PLDK7K0';

// Safe localStorage operations with fallbacks
const safeLocalStorage = {
  getItem: (key: string): string | null => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        return localStorage.getItem(key);
      }
    } catch (error) {
      console.warn('localStorage getItem failed:', error);
      // Report to Sentry only if not in development
      if (!isDevelopment()) {
        Sentry.captureException(error, {
          tags: { component: 'analytics', operation: 'localStorage_get' },
          extra: { key }
        });
      }
    }
    return null;
  },
  setItem: (key: string, value: string): void => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(key, value);
      }
    } catch (error) {
      console.warn('localStorage setItem failed:', error);
      // Report to Sentry only if not in development
      if (!isDevelopment()) {
        Sentry.captureException(error, {
          tags: { component: 'analytics', operation: 'localStorage_set' },
          extra: { key, value: value.substring(0, 100) } // Truncate value for privacy
        });
      }
    }
  }
};

// Safe sessionStorage operations with fallbacks
const safeSessionStorage = {
  getItem: (key: string): string | null => {
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        return sessionStorage.getItem(key);
      }
    } catch (error) {
      console.warn('sessionStorage getItem failed:', error);
      // Report to Sentry only if not in development
      if (!isDevelopment()) {
        Sentry.captureException(error, {
          tags: { component: 'analytics', operation: 'sessionStorage_get' },
          extra: { key }
        });
      }
    }
    return null;
  },
  setItem: (key: string, value: string): void => {
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        sessionStorage.setItem(key, value);
      }
    } catch (error) {
      console.warn('sessionStorage setItem failed:', error);
      // Report to Sentry only if not in development
      if (!isDevelopment()) {
        Sentry.captureException(error, {
          tags: { component: 'analytics', operation: 'sessionStorage_set' },
          extra: { key, value: value.substring(0, 100) } // Truncate value for privacy
        });
      }
    }
  }
};

// Development environment detection
export const isDevelopment = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  // Check NODE_ENV if available (may not be available in all environments)
  const nodeEnv = typeof process !== 'undefined' && process.env ? process.env.NODE_ENV : '';
  
  return nodeEnv === 'development' || 
         window.location.hostname === 'localhost' ||
         window.location.hostname === '127.0.0.1' ||
         window.location.hostname.includes('.local');
};

// Safe gtag wrapper with development protection
const safeGtag = (...args: any[]): void => {
  try {
    // Check if analytics should be forced in development (for testing)
    const forceAnalytics = typeof window !== 'undefined' && 
                          localStorage.getItem('ENABLE_DEV_ANALYTICS') === 'true';

    // In development, log instead of track to prevent data pollution
    if (isDevelopment() && !forceAnalytics) {
      console.group('🔍 Analytics Event (localhost - not tracked)');
      console.log('Type:', args[0]);
      console.log('Action/Event:', args[1]);
      console.log('Data:', args[2]);
      console.log('💡 Tip: Run localStorage.setItem("ENABLE_DEV_ANALYTICS", "true") to enable tracking in development');
      console.groupEnd();
      return;
    }

    // Production tracking (or development with forced analytics)
    if (typeof window !== 'undefined' && window.gtag && typeof window.gtag === 'function') {
      window.gtag(...args);
      
      // Log in development when forced
      if (isDevelopment() && forceAnalytics) {
        console.log('📊 Analytics sent (dev mode):', args[1], args[2]);
      }
    }
  } catch (error) {
    console.warn('Analytics tracking failed:', error);
    
    // Report analytics failures to Sentry (only in production)
    if (!isDevelopment()) {
      Sentry.captureException(error, {
        tags: { 
          component: 'analytics', 
          operation: 'gtag_call',
          event_type: args[0],
          event_action: args[1]
        },
        extra: { 
          gtag_args: args
        }
      });
    }
    
    // Never throw errors - analytics should be silent
  }
};

// Initialize gtag if not already available
export const initGA = () => {
  try {
    if (typeof window !== 'undefined' && !window.gtag) {
      console.warn('Google Analytics not loaded');
      return;
    }
  } catch (error) {
    console.warn('GA initialization check failed:', error);
  }
};

// Page view tracking
export const trackPageView = (url: string, title?: string, restaurantId?: string) => {
  try {
    safeGtag('event', 'page_view', {
      page_location: url,
      page_title: title,
      restaurant_id: restaurantId,
    });
  } catch (error) {
    console.warn('trackPageView failed:', error);
  }
};

// Custom event tracking
export const trackEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number,
  customParameters?: Record<string, any>
) => {
  try {
    safeGtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
      ...customParameters,
    });
  } catch (error) {
    console.warn('trackEvent failed:', error);
  }
};

// E-commerce tracking
export const trackPurchase = (transactionId: string, value: number, currency: string = 'USD', items: any[]) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'purchase', {
      transaction_id: transactionId,
      value: value,
      currency: currency,
      items: items,
    });
  }
};

export const trackAddToCart = (currency: string = 'USD', value: number, items: any[]) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'add_to_cart', {
      currency: currency,
      value: value,
      items: items,
    });
  }
};

export const trackRemoveFromCart = (currency: string = 'USD', value: number, items: any[]) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'remove_from_cart', {
      currency: currency,
      value: value,
      items: items,
    });
  }
};

export const trackBeginCheckout = (currency: string = 'USD', value: number, items: any[]) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'begin_checkout', {
      currency: currency,
      value: value,
      items: items,
    });
  }
};

// User engagement tracking
export const trackUserEngagement = (engagementType: string, details?: Record<string, any>) => {
  trackEvent('user_engagement', 'engagement', engagementType, undefined, details);
};

// Menu interaction tracking
export const trackMenuInteraction = (action: string, itemId?: string, itemName?: string, category?: string) => {
  trackEvent(action, 'menu_interaction', itemName, undefined, {
    item_id: itemId,
    item_category: category,
  });
};

// Order tracking
export const trackOrderEvent = (action: string, orderId?: string, details?: Record<string, any>) => {
  trackEvent(action, 'order', orderId, undefined, details);
};

// Payment tracking
export const trackPaymentEvent = (action: string, method?: string, amount?: number, orderId?: string) => {
  trackEvent(action, 'payment', method, amount, {
    order_id: orderId,
  });
};

// Table session tracking
export const trackTableSession = (action: string, tableId?: string, qrCode?: string) => {
  trackEvent(action, 'table_session', tableId, undefined, {
    qr_code: qrCode,
  });
};

// Cart tracking
export const trackCartEvent = (action: string, itemCount?: number, totalValue?: number) => {
  trackEvent(action, 'cart', undefined, totalValue, {
    item_count: itemCount,
  });
};

// Error tracking
export const trackError = (errorType: string, errorMessage: string, page?: string) => {
  trackEvent('error', 'app_error', errorType, undefined, {
    error_message: errorMessage,
    page: page,
  });
};

// Order conflict tracking - specific for duplicate order attempts
export const trackOrderConflict = (
  conflictType: 'ACTIVE_ORDER_EXISTS' | 'ORDER_CREATION_FAILED', 
  context: {
    tableId?: string;
    existingOrderId?: string;
    existingOrderStatus?: string;
    attemptSource?: string;
    requestedItems?: number;
    restaurantId?: string;
  }
) => {
  trackEvent('order_conflict', 'business_logic_error', conflictType, undefined, {
    conflict_type: conflictType,
    table_id: context.tableId,
    existing_order_id: context.existingOrderId,
    existing_order_status: context.existingOrderStatus,
    attempt_source: context.attemptSource,
    requested_items_count: context.requestedItems,
    restaurant_id: context.restaurantId,
  });
};

// Search tracking
export const trackSearch = (searchTerm: string, resultsCount?: number) => {
  trackEvent('search', 'site_search', searchTerm, resultsCount);
};

// Navigation tracking
export const trackNavigation = (from: string, to: string, method?: string) => {
  trackEvent('navigation', 'site_navigation', `${from}_to_${to}`, undefined, {
    navigation_method: method,
  });
};

// Social sharing tracking
export const trackShare = (method: string, contentType: string, itemId?: string) => {
  trackEvent('share', 'social_sharing', method, undefined, {
    content_type: contentType,
    item_id: itemId,
  });
};

// User identification (when user logs in or provides info)
export const identifyUser = (userId: string, properties?: Record<string, any>) => {
  try {
    safeGtag('event', 'user_identification', {
      user_id: userId,
      ...properties,
    });
  } catch (error) {
    console.warn('identifyUser failed:', error);
  }
};

// Set user properties
export const setUserProperties = (properties: Record<string, any>) => {
  try {
    safeGtag('event', 'user_properties_updated', {
      custom_map: properties,
      ...properties,
    });
  } catch (error) {
    console.warn('setUserProperties failed:', error);
  }
};

// ===== ENHANCED SOCIAL DINING ANALYTICS =====

// Helper function to extract restaurant ID from various data sources
export const getRestaurantId = (orderData?: any, tableData?: any): string | undefined => {
  try {
    // Try to get restaurant ID from order data first
    if (orderData?.restaurantId) return orderData.restaurantId;
    if (orderData?.restaurant?.id) return orderData.restaurant.id;
    
    // Try to get restaurant ID from table data
    if (tableData?.restaurantId) return tableData.restaurantId;
    if (tableData?.restaurant?.id) return tableData.restaurant.id;
    
    // Try to get from localStorage (if stored from previous sessions)
    const storedRestaurantId = safeLocalStorage.getItem('current_restaurant_id');
    if (storedRestaurantId) return storedRestaurantId;
    
    return undefined;
  } catch (error) {
    console.warn('getRestaurantId failed:', error);
    return undefined;
  }
};

// Helper function to store restaurant ID for future use
export const setRestaurantId = (restaurantId: string): void => {
  try {
    if (restaurantId) {
      safeLocalStorage.setItem('current_restaurant_id', restaurantId);
    }
  } catch (error) {
    console.warn('setRestaurantId failed:', error);
  }
};

// Device and session management
export const getDeviceId = (): string => {
  try {
    if (typeof window === 'undefined') return 'unknown';
    
    let deviceId = safeLocalStorage.getItem('device_id');
    if (!deviceId) {
      deviceId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      safeLocalStorage.setItem('device_id', deviceId);
    }
    return deviceId || 'unknown';
  } catch (error) {
    console.warn('getDeviceId failed:', error);
    return 'unknown';
  }
};

export const getSessionDuration = (): number => {
  try {
    if (typeof window === 'undefined') return 0;
    
    const sessionStart = safeSessionStorage.getItem('session_start');
    if (!sessionStart) {
      const now = Date.now().toString();
      safeSessionStorage.setItem('session_start', now);
      return 0;
    }
    
    const startTime = parseInt(sessionStart);
    return isNaN(startTime) ? 0 : Date.now() - startTime;
  } catch (error) {
    console.warn('getSessionDuration failed:', error);
    return 0;
  }
};

export const getTableSessionDuration = (tableId: string, restaurantId?: string): number => {
  try {
    if (typeof window === 'undefined') return 0;
    
    // Include restaurant ID to ensure unique table sessions across restaurants
    const key = restaurantId ? `table_session_${restaurantId}_${tableId}` : `table_session_${tableId}`;
    const sessionStart = safeLocalStorage.getItem(key);
    if (!sessionStart) {
      const now = Date.now().toString();
      safeLocalStorage.setItem(key, now);
      return 0;
    }
    
    const startTime = parseInt(sessionStart);
    return isNaN(startTime) ? 0 : Date.now() - startTime;
  } catch (error) {
    console.warn('getTableSessionDuration failed:', error);
    return 0;
  }
};

export const getPaymentPageVisitCount = (orderId: string, restaurantId?: string): number => {
  try {
    if (typeof window === 'undefined') return 0;
    
    // Include restaurant ID to ensure unique order tracking across restaurants
    const key = restaurantId ? `payment_visits_${restaurantId}_${orderId}` : `payment_visits_${orderId}`;
    const count = safeSessionStorage.getItem(key);
    const currentCount = count ? parseInt(count) : 0;
    const newCount = isNaN(currentCount) ? 1 : currentCount + 1;
    safeSessionStorage.setItem(key, newCount.toString());
    return newCount;
  } catch (error) {
    console.warn('getPaymentPageVisitCount failed:', error);
    return 1; // Default to 1 for first visit
  }
};

// Generosity scoring
export const getStoredGenerosityScore = (userId: string, restaurantId?: string): number => {
  try {
    if (typeof window === 'undefined') return 0;
    
    // Include restaurant ID for restaurant-specific generosity scores
    const key = restaurantId ? `generosity_${restaurantId}_${userId}` : `generosity_${userId}`;
    const score = safeLocalStorage.getItem(key);
    const parsedScore = score ? parseFloat(score) : 0;
    return isNaN(parsedScore) ? 0 : parsedScore;
  } catch (error) {
    console.warn('getStoredGenerosityScore failed:', error);
    return 0;
  }
};

export const updateGenerosityScore = (userId: string, socialAmount: number, totalAmount: number, restaurantId?: string): number => {
  try {
    if (typeof window === 'undefined') return 0;
    if (totalAmount <= 0) return 0; // Prevent division by zero
    
    const currentScore = getStoredGenerosityScore(userId, restaurantId);
    const generosityRatio = socialAmount / totalAmount;
    const newScore = (currentScore * 0.8) + (generosityRatio * 0.2); // Weighted average
    
    const key = restaurantId ? `generosity_${restaurantId}_${userId}` : `generosity_${userId}`;
    safeLocalStorage.setItem(key, newScore.toString());
    
    return newScore;
  } catch (error) {
    console.warn('updateGenerosityScore failed:', error);
    return 0;
  }
};

export const getLifetimeSocialPayments = (userId: string, restaurantId?: string): number => {
  try {
    if (typeof window === 'undefined') return 0;
    
    const key = restaurantId ? `lifetime_social_${restaurantId}_${userId}` : `lifetime_social_${userId}`;
    const amount = safeLocalStorage.getItem(key);
    const parsedAmount = amount ? parseFloat(amount) : 0;
    return isNaN(parsedAmount) ? 0 : parsedAmount;
  } catch (error) {
    console.warn('getLifetimeSocialPayments failed:', error);
    return 0;
  }
};

export const updateLifetimeSocialPayments = (userId: string, amount: number, restaurantId?: string): number => {
  try {
    if (typeof window === 'undefined') return 0;
    if (amount < 0) return getLifetimeSocialPayments(userId, restaurantId); // Don't allow negative amounts
    
    const key = restaurantId ? `lifetime_social_${restaurantId}_${userId}` : `lifetime_social_${userId}`;
    const current = getLifetimeSocialPayments(userId, restaurantId);
    const newAmount = current + amount;
    safeLocalStorage.setItem(key, newAmount.toString());
    
    return newAmount;
  } catch (error) {
    console.warn('updateLifetimeSocialPayments failed:', error);
    return 0;
  }
};

export const getSocialPaymentFrequency = (userId: string, restaurantId?: string): number => {
  try {
    if (typeof window === 'undefined') return 0;
    
    const key = restaurantId ? `social_frequency_${restaurantId}_${userId}` : `social_frequency_${userId}`;
    const frequency = safeLocalStorage.getItem(key);
    const parsedFrequency = frequency ? parseInt(frequency) : 0;
    return isNaN(parsedFrequency) ? 0 : parsedFrequency;
  } catch (error) {
    console.warn('getSocialPaymentFrequency failed:', error);
    return 0;
  }
};

export const incrementSocialPaymentFrequency = (userId: string, restaurantId?: string): number => {
  try {
    if (typeof window === 'undefined') return 0;
    
    const key = restaurantId ? `social_frequency_${restaurantId}_${userId}` : `social_frequency_${userId}`;
    const current = getSocialPaymentFrequency(userId, restaurantId);
    const newFrequency = current + 1;
    safeLocalStorage.setItem(key, newFrequency.toString());
    
    return newFrequency;
  } catch (error) {
    console.warn('incrementSocialPaymentFrequency failed:', error);
    return 1; // Default to 1 if failed
  }
};

export const categorizeGenerosity = (othersAmount: number, totalAmount: number): string => {
  const ratio = othersAmount / totalAmount;
  if (ratio === 0) return 'individual';
  if (ratio < 0.25) return 'low_generosity';
  if (ratio < 0.5) return 'medium_generosity';
  if (ratio < 0.75) return 'high_generosity';
  return 'full_generosity';
};

// Enhanced tracking with user context
export const trackWithUserContext = (
  action: string,
  category: string,
  label?: string,
  value?: number,
  customParams?: Record<string, any>,
  currentUser?: any,
  tableId?: string,
  restaurantId?: string
) => {
  try {
    const enhancedParams = {
      ...customParams,
      user_id: currentUser?.id,
      user_name: currentUser?.name,
      device_id: getDeviceId(),
      table_id: tableId,
      restaurant_id: restaurantId,
      session_duration: getSessionDuration()
    };
    
    safeGtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
      ...enhancedParams,
    });
  } catch (error) {
    console.warn('trackWithUserContext failed:', error);
  }
};

// Social payment tracking
interface BeneficiaryData {
  userId: string;
  userName: string;
  amount: number;
}

export const trackSocialPayment = (
  payerUserId: string,
  payerUserName: string,
  beneficiariesData: BeneficiaryData[],
  totalAmount: number,
  tableId?: string,
  restaurantId?: string
) => {
  try {
    if (totalAmount <= 0 || !beneficiariesData.length) return; // Validation
    
    const othersAmount = beneficiariesData.reduce((sum, b) => sum + (b.amount || 0), 0);
    const generosityScore = updateGenerosityScore(payerUserId, othersAmount, totalAmount, restaurantId);
    const lifetimeAmount = updateLifetimeSocialPayments(payerUserId, othersAmount, restaurantId);
    const frequency = incrementSocialPaymentFrequency(payerUserId, restaurantId);
    
    safeGtag('event', 'social_payment_initiated', {
      event_category: 'group_dining',
      event_label: payerUserId,
      value: totalAmount,
      payer_id: payerUserId,
      payer_name: payerUserName,
      beneficiaries: beneficiariesData,
      beneficiaries_count: beneficiariesData.length,
      generosity_score: generosityScore,
      social_payment_ratio: totalAmount > 0 ? othersAmount / totalAmount : 0,
      lifetime_social_amount: lifetimeAmount,
      social_payment_frequency: frequency,
      table_id: tableId,
      restaurant_id: restaurantId,
      device_id: getDeviceId()
    });
    
    // Track generosity level change
    trackGenerosityScore(payerUserId, payerUserName, 'increased', generosityScore, restaurantId);
  } catch (error) {
    console.warn('trackSocialPayment failed:', error);
  }
};

// Track generosity patterns
export const trackGenerosityScore = (
  userId: string,
  userName: string,
  action: 'increased' | 'decreased',
  currentScore: number,
  restaurantId?: string
) => {
  try {
    safeGtag('event', 'generosity_updated', {
      event_category: 'social_behavior',
      event_label: userId,
      value: currentScore,
      user_id: userId,
      user_name: userName,
      action: action,
      lifetime_paid_for_others: getLifetimeSocialPayments(userId, restaurantId),
      payment_frequency: getSocialPaymentFrequency(userId, restaurantId),
      restaurant_id: restaurantId,
      device_id: getDeviceId()
    });
  } catch (error) {
    console.warn('trackGenerosityScore failed:', error);
  }
};

// Table-level analytics
export const analyzePaymentPatterns = (orderData: any, restaurantId?: string) => {
  try {
    const socialPaymentsCount = orderData.payments?.filter((p: any) => p.beneficiaries?.length > 0)?.length || 0;
    const totalPayments = orderData.payments?.length || 1;
    
    let type: 'individual' | 'group_payer' | 'split' = 'individual';
    if (socialPaymentsCount === totalPayments) {
      type = 'group_payer';
    } else if (socialPaymentsCount > 0) {
      type = 'split';
    }
    
    const averageGenerosity = orderData.users?.reduce((sum: number, user: any) => {
      return sum + getStoredGenerosityScore(user.id, restaurantId);
    }, 0) / (orderData.users?.length || 1);
    
    return {
      type,
      socialPaymentsCount,
      averageGenerosity: isNaN(averageGenerosity) ? 0 : averageGenerosity
    };
  } catch (error) {
    console.warn('analyzePaymentPatterns failed:', error);
    return {
      type: 'individual' as const,
      socialPaymentsCount: 0,
      averageGenerosity: 0
    };
  }
};

export const getUniqueUsersAtTable = (orderData: any): any[] => {
  const users = new Map();
  
  orderData.items?.forEach((item: any) => {
    if (item.user) {
      users.set(item.user.id, item.user);
    }
  });
  
  return Array.from(users.values());
};

export const calculateTableConversionRate = (tableId: string, restaurantId?: string): number => {
  try {
    // This would typically come from your backend, but for now we'll estimate
    const visitKey = restaurantId ? `table_visits_${restaurantId}_${tableId}` : `table_visits_${tableId}`;
    const paymentKey = restaurantId ? `table_payments_${restaurantId}_${tableId}` : `table_payments_${tableId}`;
    
    const visits = parseInt(safeLocalStorage.getItem(visitKey) || '1');
    const payments = parseInt(safeLocalStorage.getItem(paymentKey) || '0');
    
    const visitCount = isNaN(visits) ? 1 : visits;
    const paymentCount = isNaN(payments) ? 0 : payments;
    
    return visitCount > 0 ? paymentCount / visitCount : 0;
  } catch (error) {
    console.warn('calculateTableConversionRate failed:', error);
    return 0;
  }
};

export const trackTableMetrics = (tableId: string, orderData: any, currentUser?: any, restaurantId?: string) => {
  try {
    const tableUsers = getUniqueUsersAtTable(orderData);
    const paymentPatterns = analyzePaymentPatterns(orderData, restaurantId);
    const sessionDuration = getTableSessionDuration(tableId, restaurantId);
    
    safeGtag('event', 'table_session_analysis', {
      event_category: 'group_dynamics',
      event_label: tableId,
      table_id: tableId,
      restaurant_id: restaurantId,
      total_users: tableUsers.length,
      total_items_ordered: orderData.items?.length || 0,
      total_amount: orderData.total || 0,
      payment_pattern: paymentPatterns.type,
      conversion_rate: calculateTableConversionRate(tableId, restaurantId),
      session_duration: sessionDuration,
      social_payments_count: paymentPatterns.socialPaymentsCount,
      average_generosity: paymentPatterns.averageGenerosity,
      current_user_id: currentUser?.id,
      device_id: getDeviceId()
    });
  } catch (error) {
    console.warn('trackTableMetrics failed:', error);
  }
};

// Enhanced payment button tracking
interface PaymentButtonData {
  orderId: string;
  totalAmount: number;
  selfAmount: number;
  othersAmount: number;
  payingForUsers: Array<{id: string, name: string, amount: number}>;
}

export const trackPaymentButtonClick = (
  paymentData: PaymentButtonData,
  currentUser?: any,
  tableId?: string,
  restaurantId?: string
) => {
  try {
    const isSocialPayment = paymentData.othersAmount > 0;
    const generosityLevel = categorizeGenerosity(paymentData.othersAmount, paymentData.totalAmount);
    
    safeGtag('event', 'payment_button_clicked', {
      event_category: 'conversion',
      event_label: paymentData.orderId,
      value: paymentData.totalAmount,
      user_id: currentUser?.id,
      user_name: currentUser?.name,
      order_id: paymentData.orderId,
      table_id: tableId,
      restaurant_id: restaurantId,
      is_social_payment: isSocialPayment,
      self_amount: paymentData.selfAmount,
      others_amount: paymentData.othersAmount,
      paying_for_count: paymentData.payingForUsers.length,
      paying_for_details: paymentData.payingForUsers,
      payment_type: isSocialPayment ? 'social' : 'individual',
      generosity_level: generosityLevel,
      device_id: getDeviceId()
    });
    
    // If it's a social payment, track the social payment details
    if (isSocialPayment && currentUser) {
      const beneficiariesData: BeneficiaryData[] = paymentData.payingForUsers.map(user => ({
        userId: user.id,
        userName: user.name,
        amount: user.amount
      }));
      
      trackSocialPayment(
        currentUser.id,
        currentUser.name,
        beneficiariesData,
        paymentData.totalAmount,
        tableId,
        restaurantId
      );
    }
  } catch (error) {
    console.warn('trackPaymentButtonClick failed:', error);
  }
};

// Enhanced bill review tracking
export const trackBillReview = (
  orderId: string,
  totalAmount: number,
  myItemsCount: number,
  othersItemsCount: number,
  currentUser?: any,
  tableId?: string,
  restaurantId?: string
) => {
  try {
    const visitNumber = getPaymentPageVisitCount(orderId, restaurantId);
    
    safeGtag('event', 'bill_reviewed', {
      event_category: 'payment_flow',
      event_label: orderId,
      value: totalAmount,
      user_id: currentUser?.id,
      user_name: currentUser?.name,
      order_id: orderId,
      table_id: tableId,
      restaurant_id: restaurantId,
      total_amount: totalAmount,
      my_items_count: myItemsCount,
      others_items_count: othersItemsCount,
      total_items_count: myItemsCount + othersItemsCount,
      visit_number: visitNumber,
      can_pay_for_others: othersItemsCount > 0,
      device_id: getDeviceId(),
      session_duration: getSessionDuration()
    });
  } catch (error) {
    console.warn('trackBillReview failed:', error);
  }
};

// Track table visit increments
export const incrementTableVisit = (tableId: string, restaurantId?: string): void => {
  try {
    if (typeof window === 'undefined') return;
    
    const key = restaurantId ? `table_visits_${restaurantId}_${tableId}` : `table_visits_${tableId}`;
    const current = parseInt(safeLocalStorage.getItem(key) || '0');
    const newCount = isNaN(current) ? 1 : current + 1;
    safeLocalStorage.setItem(key, newCount.toString());
  } catch (error) {
    console.warn('incrementTableVisit failed:', error);
  }
};

export const incrementTablePayment = (tableId: string, restaurantId?: string): void => {
  try {
    if (typeof window === 'undefined') return;
    
    const key = restaurantId ? `table_payments_${restaurantId}_${tableId}` : `table_payments_${tableId}`;
    const current = parseInt(safeLocalStorage.getItem(key) || '0');
    const newCount = isNaN(current) ? 1 : current + 1;
    safeLocalStorage.setItem(key, newCount.toString());
  } catch (error) {
    console.warn('incrementTablePayment failed:', error);
  }
};

// ===== SENTRY ERROR TRACKING =====

// Manual error tracking for business logic errors
export const captureAppError = (
  error: Error | string,
  context: {
    component?: string;
    operation?: string;
    userId?: string;
    orderId?: string;
    tableId?: string;
    restaurantId?: string;
    severity?: 'low' | 'medium' | 'high' | 'critical';
    extra?: Record<string, any>;
  } = {}
) => {
  try {
    // Always log to console
    console.error('App Error:', error, context);
    
    // Only send to Sentry in production
    if (!isDevelopment()) {
      const errorObj = error instanceof Error ? error : new Error(error);
      
      Sentry.captureException(errorObj, {
        tags: {
          component: context.component || 'unknown',
          operation: context.operation || 'unknown',
          severity: context.severity || 'medium'
        },
        user: context.userId ? { id: context.userId } : undefined,
        extra: {
          order_id: context.orderId,
          table_id: context.tableId,
          restaurant_id: context.restaurantId,
          ...context.extra
        }
      });
    }
  } catch (trackingError) {
    console.warn('Error tracking failed:', trackingError);
  }
};

// Track payment-specific errors
export const capturePaymentError = (
  error: Error | string,
  paymentContext: {
    orderId?: string;
    paymentId?: string;
    provider?: string;
    amount?: number;
    currency?: string;
    stage?: 'initiation' | 'processing' | 'callback' | 'completion';
    userId?: string;
    tableId?: string;
    restaurantId?: string;
  }
) => {
  captureAppError(error, {
    component: 'payment',
    operation: `payment_${paymentContext.stage || 'unknown'}`,
    severity: 'high',
    userId: paymentContext.userId,
    orderId: paymentContext.orderId,
    tableId: paymentContext.tableId,
    restaurantId: paymentContext.restaurantId,
    extra: {
      payment_id: paymentContext.paymentId,
      provider: paymentContext.provider,
      amount: paymentContext.amount,
      currency: paymentContext.currency,
      payment_stage: paymentContext.stage
    }
  });
};

// Track order-specific errors
export const captureOrderError = (
  error: Error | string,
  orderContext: {
    orderId?: string;
    operation?: string;
    userId?: string;
    tableId?: string;
    restaurantId?: string;
    itemsCount?: number;
  }
) => {
  captureAppError(error, {
    component: 'order',
    operation: orderContext.operation || 'unknown',
    severity: 'medium',
    userId: orderContext.userId,
    orderId: orderContext.orderId,
    tableId: orderContext.tableId,
    restaurantId: orderContext.restaurantId,
    extra: {
      items_count: orderContext.itemsCount
    }
  });
}; 